/**
 * InfinitSales - Lista de conversas COMPARTILHADA (shell do inbox, 2026-09-23)
 *
 * PARIDADE VISUAL com os ChatLists de WhatsApp: mesma busca (anel de foco,
 * spinner, ×, Ctrl+F), mesmas pílulas de filtro (Tudo / Não lidas / Funil /
 * Filtrar + drawer), mesmo cartão (badges de responsável/funil ACIMA do nome,
 * nome 17px + hora à direita, preview com check/ícone de mídia + badge de não
 * lidas) e mesma virtualização.
 *
 * O que e do CANAL vem do adaptador: chave opaca (`contact_ref`), servico de
 * busca, @handle (so Instagram), relogio da janela de 24 h (so canais da
 * Meta), tipos de midia do preview e o seletor de conexao (widget de origem,
 * analogo do numero do WhatsApp). Etiquetas vem do MESMO getLabels +
 * getContactsLabelsBatch por contact_ref; responsavel por lote de refs.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { agentAnswersInList, createdAtOf } from '../../utils/autopilotScope';
import { FixedSizeList, areEqual } from 'react-window';
import {
  Search, User, Check, Loader2, Mic, Video, FileText, Clock,
  Bot, ChevronDown, GitBranch, Filter, Camera, UserCheck,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
import assignmentService from '../../services/global/assignmentService';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import authService from '../../services/global/authService';
import requestCache from '../../services/global/requestCache';
import ChatFilterDrawer from '../labels/ChatFilterDrawer';
import WhoAnswersBadge from './WhoAnswersBadge';
import FunnelStageChips, { resolveStageEntries } from '../labels/FunnelStageChips';
import useCustomFieldFilter from '../../hooks/useCustomFieldFilter';
import { transferidoBadge } from '../../utils/handoffBadge';
import { previewText } from '../../utils/messagePlaceholder';

// Mesma altura do cartão do WhatsApp (linha de badges + nome + preview).
const ROW_HEIGHT = 92;

const MEDIA_CONFIG = {
  image: { Icon: Camera, label: 'Foto' },
  video: { Icon: Video, label: 'Vídeo' },
  audio: { Icon: Mic, label: 'Áudio' },
  file: { Icon: FileText, label: 'Arquivo' },
  interactive: { Icon: FileText, label: 'Botões' },
};

// Selo de quem responde (responsável ou agente automático): um componente
// para a lista, o cabeçalho e a ficha dos 5 canais (WhoAnswersBadge).

// Linha do FixedSizeList, memoizada no padrao do react-window (itemData +
// areEqual). Tudo que a linha le vem por `data`.
const ChatRow = React.memo(function ChatRow({ index, style, data }) {
  const {
    adapter, refOf,
    filteredChats, hasMore, searchTerm, loadingMore, onLoadMore, colors, styles,
    selectedChatId, getPreview, labelsByRef, assignedByRef, isChatHandoff,
    isChatAutopilot, labelsById, funnels, onSelectChat, activeAgentName,
    formatChatTime,
    onHoverChat,
  } = data;

  if (hasMore && !searchTerm && index === filteredChats.length) {
    return (
      <div
        style={{
          ...style,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8,
          color: loadingMore ? colors.textSecondary : colors.accentPrimary,
          cursor: loadingMore ? 'default' : 'pointer',
          fontSize: 14, fontWeight: 500,
          pointerEvents: loadingMore ? 'none' : 'auto',
        }}
        onClick={loadingMore ? undefined : onLoadMore}
      >
        {loadingMore ? (
          <>
            <Loader2 size={16} style={{ animation: 'chatlist-spin 0.8s linear infinite' }} />
            Carregando conversas...
          </>
        ) : (
          <>
            <ChevronDown size={18} />
            Carregar mais conversas
          </>
        )}
      </div>
    );
  }

  const chat = filteredChats[index];
  const isSelected = !!selectedChatId && selectedChatId === chat.id;
  const displayName = adapter.service.getChatDisplayName(chat);
  const preview = getPreview(chat);
  const hasUnread = chat.unread_count > 0;
  const contactRef = refOf(chat);
  const chatLabelIds = labelsByRef[contactRef] || [];
  const windowClosed = adapter.windowed && !adapter.service.isWindowOpen(chat);
  const chatAssigned = assignedByRef[contactRef];
  const handoff = isChatHandoff(chat);
  const transferido = transferidoBadge({ handoff, assigned: chatAssigned });
  const showAutopilot = !handoff && !chatAssigned && isChatAutopilot(chat);
  const funnelLabel = resolveStageEntries(chatLabelIds, labelsById, funnels)
    .find((e) => e.funnelName)?.funnelName || null;
  const secondary = adapter.rowSecondary?.(chat) || null;

  return (
    <div
      style={{
        ...style,
        ...styles.chatItem,
        borderLeft: isSelected
          ? `3px solid ${colors.accentPrimary}`
          : '3px solid transparent',
        paddingLeft: '11px',
        transition: 'background-color 0.12s, border-color 0.12s',
        backgroundColor: isSelected ? `${colors.accentPrimary}14` : 'transparent',
      }}
      onClick={() => onSelectChat(chat)}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = colors.bgSecondary;
        onHoverChat?.(chat);
      }}
      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <div style={styles.avatar}>
        {chat.contact_profile_picture_url ? (
          <img
            src={chat.contact_profile_picture_url}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={styles.avatarPlaceholder}>
            <User size={24} />
          </div>
        )}
      </div>

      <div style={styles.chatInfo}>
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'nowrap',
          gap: 4, marginBottom: 2, minHeight: 18, overflow: 'hidden',
        }}>
          {transferido.show && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              backgroundColor: 'rgba(245,158,11,0.12)', color: '#b45309',
              fontSize: 10, fontWeight: 500, padding: '1px 6px',
              borderRadius: 4, flexShrink: 0, lineHeight: '16px',
            }} title={transferido.title}>
              <UserCheck size={10} />
              Transferido
            </span>
          )}
          <WhoAnswersBadge
            assigned={chatAssigned}
            autoAgentName={showAutopilot ? (activeAgentName || 'Agente IA') : null}
          />
          {funnelLabel && (
            <FunnelStageChips
              labelIds={chatLabelIds}
              labelsById={labelsById}
              funnels={funnels}
              colors={colors}
            />
          )}
          {secondary && (
            <span style={{
              fontSize: 10, color: colors.textTertiary, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginLeft: 'auto', maxWidth: 120,
            }} title={secondary}>
              {secondary}
            </span>
          )}
        </div>

        <div style={styles.chatHeader}>
          <span style={{ ...styles.chatName, display: 'block', minWidth: 0 }}>
            {displayName}
          </span>
          <span style={{ ...styles.chatTime, ...(hasUnread && { color: colors.accentPrimary, fontWeight: 500 }) }}>
            {formatChatTime(chat.last_message_at)}
          </span>
        </div>

        <div style={styles.chatPreview}>
          {/* Check ÚNICO: o doc de chat não guarda o status da última
              mensagem — entregue/lida aparecem na bolha. */}
          {chat.last_message_from_me && (
            <Check size={16} style={{ color: colors.textTertiary, flexShrink: 0 }} />
          )}
          {preview.Icon && (
            <preview.Icon size={16} style={{ flexShrink: 0, color: colors.textTertiary }} />
          )}
          <span style={{
            ...styles.chatPreviewText,
            ...(preview.isMedia ? { color: colors.textTertiary } : {}),
          }}>
            {preview.text || adapter.texts.previewFallback?.(chat) || 'Clique para ver a conversa'}
          </span>
          {windowClosed && !hasUnread && (
            <Clock
              size={13}
              color={colors.textTertiary}
              style={{ flexShrink: 0 }}
              aria-label="Janela de 24 horas fechada"
            />
          )}
          {hasUnread && (
            <div style={styles.chatBadge}>
              {chat.unread_count > 99 ? '99+' : chat.unread_count}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}, areEqual);


const ChatList = ({
  adapter,
  onHoverChat,
  initialScrollOffset = 0,
  onScrollOffset,
  chats = [],
  selectedChat,
  onSelectChat,
  loading = false,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  account = null,
  onExtraFiltersChange,
  channelFilter = null,
  onChannelFilterChange,
  autopilotContacts = [],
  autopilotMode = 'off',
  blockedContacts = [],
  activeAgentName = null,
  // Pausa do agente por canal (2026-10-14): sem selo de agente neste canal.
  channelPaused = false,
  autopilotSince = null,
  handoffChats = [],
  onSearchTermChange,
}) => {
  const { colors } = useTheme();
  const prefix = adapter.prefix;
  const keyField = adapter.keyField;
  const service = adapter.service;
  const refOf = useCallback((chat) => chat?.contact_ref
    || (chat?.[keyField] ? `${prefix}${chat[keyField]}` : ''), [prefix, keyField]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [serverResults, setServerResults] = useState([]);
  // Teto da busca global (2026-10-20): o servidor diz se ha mais que 40.
  const [serverHasMore, setServerHasMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState('all');
  const [labelsById, setLabelsById] = useState({});
  const [labelsByRef, setLabelsByRef] = useState({});
  const [funnels, setFunnels] = useState([]);
  const [selectedLabelFilters, setSelectedLabelFilters] = useState(new Set());
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [drawerFocus, setDrawerFocus] = useState(null);
  const [searchInMessages, setSearchInMessages] = useState(false);
  const [filterSince, setFilterSince] = useState('');
  const [filterCreated, setFilterCreated] = useState({ from: '', to: '' });
  const [filterUnanswered, setFilterUnanswered] = useState(false);
  const [filterMember, setFilterMember] = useState('');
  const [assignTargets, setAssignTargets] = useState([]);
  useEffect(() => {
    let alive = true;
    assignmentService.getTargets()
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res) ? res : (res?.targets || res?.data || []);
        setAssignTargets(
          (list || [])
            .map((t) => ({ id: t.id, name: t.name || t.label || t.id, type: t.type }))
            .filter((t) => t.id && t.name)
        );
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  const windowRef = useRef(null);
  const lastSelectedIndexRef = useRef(-1);

  // Responsável por contato — mapa {contact_ref: assigned_to} no NÍVEL DA
  // LISTA. Cada ref é buscado UMA vez por montagem; o SSE aplica o novo
  // responsável direto do payload, sem refetch.
  const [assignedByRef, setAssignedByRef] = useState({});
  const fetchedRefsRef = useRef(new Set());
  const { flags: featureFlags, loading: flagsLoading } = useFeatureFlags();
  const responsavelPorLote = Boolean(featureFlags?.assignments_by_refs);
  const handoffNaLista = Boolean(featureFlags?.inbox_meta_handoff_list);

  useSSEEvent(SSE_EVENTS.CONTACT_ASSIGNMENT_CHANGED, useCallback((data) => {
    const ref = String(data?.contact_ref || data?.phone || '');
    if (!ref.startsWith(prefix)) return;
    setAssignedByRef((prev) => ({ ...prev, [ref]: data?.assigned_to ?? null }));
  }, [prefix]));

  // Filtros extras (server-side no Inbox)
  const campos = useCustomFieldFilter();

  // Busca DENTRO dos filtros (INBOX_SEARCH_WITHIN_FILTERS_ENABLED): com etapa,
  // responsavel, data, campo ou "sem resposta" ativo, o termo vai para a lista
  // filtrada no servidor (`q`, intersecao) e a busca global (uniao, que ignora
  // os filtros) nao roda. Relato do Victor (myCar, 24/09): "daniel" com 2
  // etapas mostrava 41 conversas, so 1 na etapa.
  const flagBuscaNosFiltros = Boolean(featureFlags?.inbox_search_within_filters);
  const filtroNoServidor = selectedLabelFilters.size > 0 || Boolean(filterMember) || Boolean(filterSince)
    || Boolean(filterCreated.from) || Boolean(filterCreated.to) || filterUnanswered
    || (campos.values || []).length > 0
    || filter === 'unread';
  const buscaNosFiltros = flagBuscaNosFiltros && filtroNoServidor;
  // O Inbox guarda o termo (debounce igual ao da busca) e so o usa com filtro.
  useEffect(() => {
    if (!flagBuscaNosFiltros) return undefined;
    const term = (searchTerm || '').trim();
    const t = setTimeout(() => onSearchTermChange?.(term.length >= 2 ? term : '', searchInMessages), 300);
    return () => clearTimeout(t);
  }, [flagBuscaNosFiltros, searchTerm, searchInMessages, onSearchTermChange]);
  const notifyExtraFilters = useCallback((next) => {
    const preset = (next.since ?? filterSince) || '';
    let sinceISO = null;
    if (preset) {
      const d = new Date();
      if (preset === 'today') d.setHours(0, 0, 0, 0);
      else if (preset === '7d') d.setDate(d.getDate() - 7);
      else if (preset === '30d') d.setDate(d.getDate() - 30);
      sinceISO = d.toISOString();
    }
    onExtraFiltersChange?.({
      since: sinceISO,
      created_from: (next.created ?? filterCreated).from || null,
      created_to: (next.created ?? filterCreated).to || null,
      unanswered: next.unanswered ?? filterUnanswered,
      unread: next.unread ?? (filter === 'unread'),
      custom_fields: next.customFields ?? campos.values,
      member_id: (next.member ?? filterMember) || null,
      label_ids: Array.from(next.labels ?? selectedLabelFilters),
    });
  }, [onExtraFiltersChange, filterSince, filterCreated, filterUnanswered, campos.values, filterMember, selectedLabelFilters, filter]);
  // Chip "Nao lidas" no servidor (2026-10-20): antes so filtrava a pagina carregada.
  const filterChipRef = useRef(filter);
  useEffect(() => {
    if (filterChipRef.current === filter) return;
    const antes = filterChipRef.current;
    filterChipRef.current = filter;
    if (antes === 'unread' || filter === 'unread') notifyExtraFilters({ unread: filter === 'unread' });
  }, [filter, notifyExtraFilters]);
  const handleChangeMember = useCallback((v) => {
    setFilterMember(v || ''); notifyExtraFilters({ member: v || '' });
  }, [notifyExtraFilters]);
  const handleChangeLabelFilters = useCallback((next) => {
    const set = next instanceof Set ? next : new Set(next);
    setSelectedLabelFilters(set);
    notifyExtraFilters({ labels: set });
  }, [notifyExtraFilters]);
  const handleChangeSince = useCallback((v) => {
    setFilterSince(v || ''); notifyExtraFilters({ since: v || '' });
  }, [notifyExtraFilters]);
  const handleChangeCreatedRange = useCallback((from, to) => {
    const next = { from: from || '', to: to || '' };
    setFilterCreated(next); notifyExtraFilters({ created: next });
  }, [notifyExtraFilters]);
  const handleChangeUnanswered = useCallback((v) => {
    setFilterUnanswered(!!v); notifyExtraFilters({ unanswered: !!v });
  }, [notifyExtraFilters]);
  const handleClearExtra = useCallback(() => {
    setFilterSince(''); setFilterCreated({ from: '', to: '' }); setFilterUnanswered(false); setFilterMember('');
    onExtraFiltersChange?.({ since: null, created_from: null, created_to: null, unanswered: false, member_id: null, label_ids: Array.from(selectedLabelFilters) });
  }, [onExtraFiltersChange, selectedLabelFilters]);

  // Etiquetas/funis — catálogo por getLabels; chips em lote por contact_ref.
  const labelsByIdRef = useRef({});
  const labelsRefetchTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(labelsRefetchTimerRef.current), []);

  const refreshLabels = useCallback(async () => {
    try {
      authService.listFunnels()
        .then((fs) => setFunnels(Array.isArray(fs) ? fs : []))
        .catch(() => {});
      const res = await authService.getLabels();
      const lista = res?.data || res;
      const map = {};
      (Array.isArray(lista) ? lista : []).forEach((l) => { map[l.id] = l; });
      setLabelsById(map);
      labelsByIdRef.current = map;
    } catch (err) {
      console.warn(`ChatList(${adapter.code}): failed to load labels`, err);
    }
  }, [adapter.code]);

  useEffect(() => { refreshLabels(); }, [refreshLabels]);

  const refsKey = useMemo(
    () => Array.from(new Set(
      [...(chats || []), ...(Array.isArray(serverResults) ? serverResults : [])]
        .map(refOf).filter(Boolean)
    )).sort().join(','),
    [chats, serverResults, refOf],
  );
  useEffect(() => {
    if (!refsKey) return undefined;
    let cancelado = false;
    const t = setTimeout(async () => {
      try {
        const refs = refsKey.split(',');
        const mapa = await authService.getContactsLabelsBatch(refs);
        if (cancelado) return;
        setLabelsByRef((prev) => {
          const next = { ...prev };
          refs.forEach((ref) => {
            const ids = mapa?.[ref];
            if (Array.isArray(ids) && ids.length) next[ref] = ids; else delete next[ref];
          });
          return next;
        });
      } catch (err) {
        console.warn(`ChatList(${adapter.code}): failed to load contact labels`, err);
      }
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [refsKey, adapter.code]);

  useSSEEvent(SSE_EVENTS.CONTACT_LABELS_CHANGED, (data) => {
    const ref = String(data?.contact_ref || '');
    const ids = Array.isArray(data?.label_ids) ? data.label_ids : null;
    if (ref.startsWith(prefix) && ids) {
      setLabelsByRef((prev) => {
        const next = { ...prev };
        if (ids.length) next[ref] = ids; else delete next[ref];
        return next;
      });
      const allKnown = ids.every((id) => labelsByIdRef.current[id]);
      if (allKnown) return;
    } else if (!ref.startsWith(prefix)) {
      return; // mudança de etiqueta de outro canal — não interessa a esta lista
    }
    clearTimeout(labelsRefetchTimerRef.current);
    labelsRefetchTimerRef.current = setTimeout(() => {
      requestCache.invalidate('labels:catalog');
      refreshLabels();
    }, 350);
  }, [refreshLabels, prefix]);

  // Busca — Ctrl+F foca, Escape limpa; server-side com debounce.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape' && searchTerm) {
        setSearchTerm('');
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchTerm]);

  useEffect(() => {
    const term = (searchTerm || '').trim();
    if (term.length < 2 || buscaNosFiltros) {
      setServerResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const resp = await service.searchChats(term, searchInMessages, campos.values, channelFilter);
        if (!cancelled) { setServerResults(Array.isArray(resp?.data) ? resp.data : []); setServerHasMore(Boolean(resp?.has_more)); }
      } catch (_e) {
        if (!cancelled) setServerResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchTerm, searchInMessages, campos.values, service, channelFilter, buscaNosFiltros]);

  const isChatHandoff = useCallback((chat) => {
    const ref = refOf(chat);
    return !!ref && handoffChats.includes(ref);
  }, [handoffChats, refOf]);

  const isChatAutopilot = useCallback((chat) => {
    const ref = refOf(chat);
    if (!ref) return false;
    // Regra unica dos 5 inboxes (utils/autopilotScope), com o carimbo "so novos".
    return agentAnswersInList(
      { mode: autopilotMode, contacts: autopilotContacts, blocked: blockedContacts, since: autopilotSince, channelPaused },
      { ref, createdAt: createdAtOf(chat) },
    );
  }, [autopilotMode, autopilotContacts, blockedContacts, refOf, channelPaused, autopilotSince]);

  const filteredChats = useMemo(() => {
    let result = chats || [];

    if (filter === 'unread') {
      result = result.filter((chat) => chat.unread_count > 0);
    } else if (filter === 'handoff') {
      result = result.filter((chat) => isChatHandoff(chat));
    } else if (filter === 'agent') {
      result = result.filter((chat) => isChatAutopilot(chat));
    }

    if (searchTerm && !buscaNosFiltros) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((chat) => {
        const name = (chat.contact_name || '').toLowerCase();
        if (name.includes(term)) return true;
        if (adapter.hasHandle) {
          const username = (chat.contact_username || '').toLowerCase();
          if (username.includes(term.replace(/^@/, ''))) return true;
        }
        const email = (chat.contact_email || '').toLowerCase();
        if (email && email.includes(term)) return true;
        if (searchInMessages) {
          const lastMsg = (chat.last_message_text || '').toLowerCase();
          if (lastMsg.includes(term)) return true;
        }
        return false;
      });

      if (serverResults.length) {
        const have = new Set(result.map((c) => c.id));
        for (const r of serverResults) {
          if (r?.id && !have.has(r.id)) { result = result.concat(r); have.add(r.id); }
        }
      }
    }

    if (selectedChat?.id) {
      const inResult = result.some((c) => c.id === selectedChat.id);
      if (!inResult) {
        const fromAll = (chats || []).find((c) => c.id === selectedChat.id);
        if (fromAll) result.push(fromAll);
      }
    }

    return result;
  }, [chats, filter, searchTerm, searchInMessages, serverResults, selectedChat, isChatHandoff, isChatAutopilot, adapter.hasHandle, buscaNosFiltros]);

  useEffect(() => {
    if (flagsLoading) return;
    const missing = filteredChats
      .map(refOf)
      .filter((r) => r && !fetchedRefsRef.current.has(r));
    if (!missing.length) return;
    missing.forEach((r) => fetchedRefsRef.current.add(r));
    const porCartao = () => Promise.all(missing.map(async (r) => {
      try {
        const raw = await assignmentService.get(r);
        const d = raw?.data ?? raw;
        return [r, d?.assigned_to ?? null];
      } catch {
        return [r, null];
      }
    }));
    const busca = responsavelPorLote
      ? assignmentService.getByRefs(missing)
        .then((mapa) => missing.map((r) => [r, mapa?.[r] ?? null]))
        .catch(() => porCartao())
      : porCartao();
    busca.then((pairs) => {
      setAssignedByRef((prev) => {
        const next = { ...prev };
        pairs.forEach(([r, a]) => { next[r] = a; });
        return next;
      });
    });
  }, [filteredChats, flagsLoading, responsavelPorLote, refOf]);

  useEffect(() => {
    if (!selectedChat?.id) {
      lastSelectedIndexRef.current = -1;
      return;
    }
    const idx = filteredChats.findIndex((c) => c.id === selectedChat.id);
    if (idx === -1) {
      lastSelectedIndexRef.current = -1;
      return;
    }
    if (idx !== lastSelectedIndexRef.current) {
      windowRef.current?.scrollToItem?.(idx, 'smart');
      lastSelectedIndexRef.current = idx;
    }
  }, [filteredChats, selectedChat]);

  const [listHeight, setListHeight] = useState(600);
  useEffect(() => {
    if (!listRef.current) return undefined;
    const el = listRef.current;
    const update = () => setListHeight(el.clientHeight || 600);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const formatChatTime = useCallback((timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } if (diffDays === 1) {
      return 'Ontem';
    } if (diffDays < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'short' });
    }
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }, []);

  const mediaConfig = useMemo(() => ({ ...MEDIA_CONFIG, ...(adapter.mediaPreview || {}) }), [adapter.mediaPreview]);
  const getPreview = useCallback((chat) => {
    const text = chat.last_message_text || '';
    const media = mediaConfig[text.replace(/[[\]]/g, '')];
    if (media) return { text: media.label, Icon: media.Icon, isMedia: true };
    return { text: previewText(text), Icon: null, isMedia: false };
  }, [mediaConfig]);

  const filterOptions = [
    { id: 'all', label: 'Tudo' },
    { id: 'unread', label: 'Não lidas' },
    ...(handoffNaLista ? [
      { id: 'handoff', label: 'Transferido' },
      { id: 'agent', label: 'Atendidos pela IA' },
    ] : []),
  ];

  const styles = useMemo(() => ({
    container: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: colors.bgPrimary },
    header: { padding: '10px 14px', borderBottom: `1px solid ${colors.borderColor}` },
    searchWrapper: {
      display: 'flex', alignItems: 'center', backgroundColor: colors.bgTertiary,
      borderRadius: '24px', padding: '0 12px', marginBottom: '10px', transition: 'box-shadow 0.2s',
      ...(searchFocused && { boxShadow: `0 0 0 2px ${colors.accentPrimary}40` }),
    },
    searchInput: { flex: 1, border: 'none', background: 'transparent', padding: '10px 12px', fontSize: '16px', color: colors.textPrimary, outline: 'none' },
    filtersContainer: { display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px', alignItems: 'center' },
    filterBtn: {
      padding: '6px 12px', border: 'none', backgroundColor: colors.bgTertiary, color: colors.textSecondary,
      borderRadius: '18px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
      whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '6px',
    },
    filterBtnActive: { backgroundColor: colors.accentPrimary, color: 'white' },
    listContainer: { flex: 1, overflowY: 'auto', overflowX: 'hidden' },
    chatItem: { display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', transition: 'background-color 0.1s' },
    avatar: { width: '50px', height: '50px', borderRadius: '50%', overflow: 'hidden', marginRight: '12px', flexShrink: 0 },
    avatarPlaceholder: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.bgTertiary, color: colors.textTertiary },
    chatInfo: { flex: 1, minWidth: 0, paddingBottom: '10px' },
    chatHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
    chatName: { fontWeight: 500, fontSize: '17px', color: colors.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' },
    chatTime: { fontSize: '12px', color: colors.textTertiary, flexShrink: 0 },
    chatPreview: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: colors.textSecondary },
    chatPreviewText: { flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    chatBadge: {
      backgroundColor: colors.accentPrimary, color: 'white', fontSize: '11px', fontWeight: 600, minWidth: '20px', height: '20px',
      padding: '0 6px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '8px',
    },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', color: colors.textTertiary, textAlign: 'center' },
    skeleton: { display: 'flex', alignItems: 'center', padding: '10px 14px' },
    skeletonAvatar: { width: '50px', height: '50px', borderRadius: '50%', backgroundColor: colors.bgTertiary, marginRight: '12px', animation: 'pulse 1.5s infinite' },
    skeletonLine: { height: '14px', borderRadius: '4px', backgroundColor: colors.bgTertiary, marginBottom: '8px', animation: 'pulse 1.5s infinite' },
  }), [colors, searchFocused]);

  const selectedChatId = selectedChat?.id || null;
  const rowData = useMemo(() => ({
    adapter, refOf,
    filteredChats, hasMore, searchTerm, loadingMore, onLoadMore, colors, styles,
    selectedChatId, getPreview, labelsByRef, assignedByRef, isChatHandoff,
    isChatAutopilot, labelsById, funnels, onSelectChat, activeAgentName,
    formatChatTime,
    onHoverChat,
  }), [
    adapter, refOf,
    filteredChats, hasMore, searchTerm, loadingMore, onLoadMore, colors, styles,
    selectedChatId, getPreview, labelsByRef, assignedByRef, isChatHandoff,
    isChatAutopilot, labelsById, funnels, onSelectChat, activeAgentName,
    formatChatTime,
    onHoverChat,
  ]);

  const ListExtras = adapter.ListExtras;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.searchWrapper}>
          <Search size={18} style={{ color: searchFocused ? colors.accentPrimary : colors.textTertiary }} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={adapter.texts.searchPlaceholder || 'Pesquisar por nome'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={styles.searchInput}
          />
          {searching && (
            <Loader2 size={16} style={{ color: colors.accentPrimary, animation: 'chatlist-spin 0.8s linear infinite', flexShrink: 0 }} />
          )}
          {searchTerm && (
            <button
              style={{ padding: '4px', border: 'none', background: 'transparent', color: colors.textTertiary, cursor: 'pointer' }}
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>

        {/* Seletor de conexao do canal (widget de origem), analogo do numero
            de origem do WhatsApp. So o canal que tem N conexoes renderiza. */}
        {ListExtras && (
          <ListExtras colors={colors} account={account} value={channelFilter} onChange={onChannelFilterChange} />
        )}

        {searchTerm && serverHasMore && !buscaNosFiltros && (
          <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 6 }}>
            Mostrando os 40 primeiros resultados da busca. Refine o termo para ver os outros.
          </div>
        )}
        {(filter === 'handoff' || filter === 'agent') && hasMore && !searchTerm && (
          <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 6 }}>
            Este filtro olha só as conversas já carregadas. Role a lista para carregar mais.
          </div>
        )}
        <div style={styles.filtersContainer}>
          {filterOptions
            .filter((f) => f.id === 'all' || f.id === 'unread')
            .map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                style={{ ...styles.filterBtn, ...(filter === f.id ? styles.filterBtnActive : {}) }}
              >
                {f.label}
              </button>
            ))}
          <button
            onClick={() => { setDrawerFocus('funnel'); setShowFilterDrawer(true); }}
            style={{ ...styles.filterBtn, ...(selectedLabelFilters.size > 0 ? styles.filterBtnActive : {}) }}
            title="Filtrar por funil"
          >
            <GitBranch size={12} />
            Funil
            {selectedLabelFilters.size > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 16, height: 16, padding: '0 5px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                backgroundColor: 'rgba(255,255,255,0.25)',
              }}>
                {selectedLabelFilters.size}
              </span>
            )}
          </button>
          {(() => {
            const drawerStatusActive = filter !== 'all' && filter !== 'unread';
            const extraActive = (filterMember ? 1 : 0) + (filterSince ? 1 : 0) + ((filterCreated.from || filterCreated.to) ? 1 : 0) + (filterUnanswered ? 1 : 0);
            const totalActive = (drawerStatusActive ? 1 : 0) + (selectedLabelFilters.size > 0 ? 1 : 0) + extraActive;
            return (
              <button
                onClick={() => { setDrawerFocus(null); setShowFilterDrawer(true); }}
                style={{ ...styles.filterBtn, ...(totalActive > 0 ? styles.filterBtnActive : {}) }}
                title="Filtrar conversas"
              >
                <Filter size={12} />
                Filtrar
                {totalActive > 0 && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 16, height: 16, padding: '0 5px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                    backgroundColor: 'rgba(255,255,255,0.25)',
                  }}>
                    {totalActive}
                  </span>
                )}
              </button>
            );
          })()}
        </div>

        {(filter !== 'all' || selectedLabelFilters.size > 0 || filterMember || filterSince || filterCreated.from || filterCreated.to || filterUnanswered) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11, color: colors.textSecondary, flexWrap: 'wrap' }}>
            <span>Filtrando por:</span>
            {filter !== 'all' && (
              <span style={{ color: colors.accentPrimary, fontWeight: 600 }}>
                {filterOptions.find((o) => o.id === filter)?.label}
              </span>
            )}
            {Array.from(selectedLabelFilters).map((id) => {
              const label = labelsById[id];
              if (!label) return null;
              return (
                <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: label.color, fontWeight: 600 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: label.color }} />
                  {label.name}
                </span>
              );
            })}
            {filterSince && (
              <span style={{ color: colors.accentPrimary, fontWeight: 600 }}>
                {filterSince === 'today' ? 'Hoje' : filterSince === '7d' ? 'Últimos 7 dias' : 'Últimos 30 dias'}
              </span>
            )}
            {(filterCreated.from || filterCreated.to) && (
              <span style={{ color: colors.accentPrimary, fontWeight: 600 }}>
                Entrou{filterCreated.from ? ` de ${filterCreated.from.split('-').reverse().join('/')}` : ''}{filterCreated.to ? ` até ${filterCreated.to.split('-').reverse().join('/')}` : ''}
              </span>
            )}
            {filterUnanswered && (
              <span style={{ color: colors.accentPrimary, fontWeight: 600 }}>Sem resposta</span>
            )}
            <button
              onClick={() => { setFilter('all'); setSelectedLabelFilters(new Set()); handleClearExtra(); }}
              style={{ background: 'transparent', border: 'none', color: colors.textTertiary, fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
            >
              limpar
            </button>
          </div>
        )}
      </div>

      <ChatFilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => { setShowFilterDrawer(false); setDrawerFocus(null); }}
        colors={colors}
        initialFocusSection={drawerFocus}
        statusOptions={filterOptions.filter((f) => f.id !== 'all' && f.id !== 'unread')}
        statusValue={filter}
        onChangeStatus={setFilter}
        labels={Object.values(labelsById)}
        funnels={funnels}
        selectedLabelIds={selectedLabelFilters}
        onChangeLabels={handleChangeLabelFilters}
        members={assignTargets}
        memberValue={filterMember}
        onChangeMember={handleChangeMember}
        sinceValue={filterSince}
        onChangeSince={handleChangeSince}
        createdFromValue={filterCreated.from}
        createdToValue={filterCreated.to}
        onChangeCreatedRange={handleChangeCreatedRange}
        unansweredValue={filterUnanswered}
        onChangeUnanswered={handleChangeUnanswered}
        onClearExtra={handleClearExtra}
        searchInMessages={searchInMessages}
        onChangeSearchInMessages={setSearchInMessages}
        customFields={campos.fields}
        customFieldValues={campos.values}
        onChangeCustomFields={(vals) => {
          campos.setValues(vals);
          notifyExtraFilters({ customFields: vals });
        }}
      />

      {searchTerm && (
        <div style={{ padding: '8px 14px', fontSize: '12px', color: colors.textTertiary, borderBottom: `1px solid ${colors.borderColor}` }}>
          {filteredChats.length} conversa{filteredChats.length !== 1 ? 's' : ''} encontrada{filteredChats.length !== 1 ? 's' : ''}
        </div>
      )}

      <div ref={listRef} style={styles.listContainer}>
        {loading && !filteredChats.length ? (
          [...Array(6)].map((_, i) => (
            <div key={i} style={styles.skeleton}>
              <div style={styles.skeletonAvatar} />
              <div style={{ flex: 1 }}>
                <div style={{ ...styles.skeletonLine, width: '65%' }} />
                <div style={{ ...styles.skeletonLine, width: '45%', height: '12px' }} />
              </div>
            </div>
          ))
        ) : filteredChats.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ width: 80, height: 80, backgroundColor: colors.bgTertiary, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Search size={32} />
            </div>
            <p style={{ fontSize: 16, fontWeight: 500, color: colors.textPrimary, marginBottom: 8 }}>
              {searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa'}
            </p>
            <p style={{ fontSize: 14 }}>
              {searchTerm
                ? (adapter.texts.searchHint || 'Tente buscar por outro nome')
                : adapter.texts.emptyHint(account)}
            </p>
          </div>
        ) : (
          <FixedSizeList
            ref={windowRef}
            height={listHeight}
            itemCount={filteredChats.length + (hasMore && !searchTerm ? 1 : 0)}
            itemSize={ROW_HEIGHT}
            itemData={rowData}
            width="100%"
            initialScrollOffset={initialScrollOffset}
            onScroll={onScrollOffset ? ({ scrollOffset }) => onScrollOffset(scrollOffset) : undefined}
          >
            {ChatRow}
          </FixedSizeList>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes chatlist-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input::placeholder { color: ${colors.textTertiary}; }
      `}</style>
    </div>
  );
};

export default ChatList;
